// account-ui catalog — locale en (register: plain/neutral).
// `account.*` lives here. common.*/errors.* keys app-kit actually defines are
// owned by @lumi/app-kit and merged underneath (mergeAppKitMessages) — redefining
// one of those is a CI failure (scripts/check-i18n-no-shadow.mjs). `common.loading`
// is not one of app-kit's keys, so it is defined locally here (issue 195, DR-19a),
// the same carve-out workspace-ui/apprentice-ui already use.
export default {
  common: {
    loading: 'Loading…',
  },
  account: {
    // Product name — deliberately identical in every locale.
    brand: 'lumi HR',
    home: {
      title: 'Your lumi HR account',
      intro:
        'This page belongs to you, not to a workspace. It stays reachable even when a workspace you belong to is locked or has been closed.',
      rightsTitle: 'What you can do here',
      rights: {
        takeout: 'Download a copy of your data once it has been prepared for you.',
        erasure:
          'Ask for a workspace and its data to be erased, or withdraw a request you filed yourself.',
        invoices: 'View and download the invoices of a workspace you administer.',
        export: 'Export a workspace you own — all of its data, in one encrypted archive.',
      },
      signInHint:
        'Each of these asks you to sign in first. A link we send you by email only points to this page — it never signs you in.',
      workspaceHint:
        'Everything else — your daily work and your settings — stays in your workspace.',
    },
    signIn: {
      title: 'Sign in',
      intro:
        'Use your lumi HR email address and password. You do not need to name a workspace — we show you the ones you belong to.',
      email: 'Email address',
      password: 'Password',
      submit: 'Sign in',
      failed: 'We could not sign you in with that email address and password.',
      passkey: 'Sign in with a passkey',
      passkeyHint:
        'No email address? An account set up with a mobile number signs in with its passkey.',
      passkeyFailed: 'We could not sign you in with that passkey.',
      operatorSecondFactor: 'This is a system admin account, which needs a second factor. Here, sign in with a passkey; with a password and an authenticator code, sign in on the lumi console.',
    },
    session: {
      signedInAs: 'Signed in as {email}.',
      signOut: 'Sign out',
    },
    workspaces: {
      title: 'Your workspaces',
      intro:
        'The workspaces you belong to. A locked workspace stays on this list — that is what this page is for.',
      emptyTitle: 'No workspaces',
      emptyBody:
        'This account does not belong to any workspace. If you expected one here, ask the person who invited you.',
      loadFailed: 'We could not load your workspaces.',
      ssoRequired:
        'This workspace requires company single sign-on. Sign in through the workspace itself to use these tools — a password does not open it here.',
      state: {
        active: 'Active',
        suspended: 'Locked',
        provisioning: 'Being set up',
        erased: 'Erased',
        unavailable: 'Unavailable',
      },
      hint: {
        active: 'This workspace is running normally.',
        suspended: 'This workspace is locked. You can still exercise your rights here.',
        provisioning: 'This workspace is still being set up.',
        erased:
          'This workspace and its data have been erased. Only its invoices remain, because we are required to keep them.',
        unavailable: 'This workspace cannot be reached at the moment.',
      },
    },
    invoices: {
      open: 'Show invoices',
      hide: 'Hide invoices',
      empty: 'There are no invoices for this workspace.',
      loadFailed: 'We could not load the invoices for this workspace.',
      download: 'Download PDF',
      documentMissing: 'There is no downloadable document for this invoice.',
      documentUnavailable:
        'The invoice document cannot be fetched right now. It still exists — please try again in a moment.',
      downloadFailed: 'We could not download that invoice.',
    },
    // The Art. 15(3) data copy (GDPR MR3 D13). The copy answers four questions and
    // no more: what the file is, that it expires and on which day, what to do once
    // it has, and what to do when there is nothing here.
    //
    // NO DURATION ANYWHERE IN THIS BLOCK. The retention window is a property of the
    // record: the date the mail and the document already state is `expiresAt` on
    // that record, and a catalogue saying "30 days" would be a third, independent
    // claim that drifts the day the window changes.
    //
    // An expired copy is the ORDINARY end of the window, not a failure: it reads as
    // a fact plus the way to ask again, and it never renders in an error tone.
    takeout: {
      title: 'Your copy of your data',
      what:
        'This is a copy of the personal data this workspace holds about you, together with an explanation of what it is used for.',
      availableUntil:
        'You can download it until {date}. After that it is deleted automatically, so save it before then if you want to keep it.',
      download: 'Download my data',
      none: 'There is no copy of your data waiting for you here.',
      expired:
        'The copy prepared for you could be downloaded until {date}. It has since been deleted, as we said it would be.',
      howToAsk:
        'You can ask for a copy at any time: contact the workspace that holds your data. Once it is ready we send you an email, and you download it on this page.',
      loadFailed: 'We could not check whether a copy of your data is waiting for you.',
      downloadUnavailable:
        'Your copy cannot be fetched right now. It still exists — please try again in a moment.',
      downloadFailed: 'We could not download your copy of the data.',
    },
    // The WORKSPACE's export (issue 303 decisions 7, 8 and 32) — the customer's
    // Art. 20 archive, not the employee's Art. 15 copy above it. The copy has
    // three jobs: say what is really in the file, make the passphrase's finality
    // unmissable BEFORE it is typed, and keep "gone" apart from "we cannot reach
    // it right now". No duration is stated anywhere: the archive's lifetime is a
    // deployment setting and reaches this screen only as `{date}`.
    export: {
      title: 'Export this workspace',
      intro:
        'Download everything in this workspace as one encrypted archive. This also works while the workspace is locked — that is what this page is for.',
      contents:
        'The archive holds every person’s record, every file uploaded to the workspace and its connection settings for other services. Treat it as the workspace itself: keep it somewhere safe and pass it to nobody who should not see all of it. The archive also includes an HR Open Standards (HR-JSON) document for People, Recruiting and Time — the shared format for taking your data to another provider.',
      passphraseWarning:
        'You choose the passphrase and we never store it. Without it the archive cannot be opened again — not by you, and not by us.',
      passphraseLabel: 'Passphrase for the archive',
      passphraseHint: 'At least {min} characters.',
      confirmLabel: 'Repeat the passphrase',
      submit: 'Start the export',
      started:
        'The export has started. Preparing it takes a while, so come back to this page and refresh the list.',
      errorTooShort: 'Choose a passphrase of at least {min} characters.',
      errorMismatch: 'The two passphrases are not the same. Check them and try again.',
      errorCannotStart:
        'The export cannot start right now — an export may already be running for this workspace, or the workspace may be being closed.',
      errorTooMany:
        'This workspace has started as many exports as it may today. Please try again tomorrow.',
      errorGeneric: 'We could not start the export. Nothing was changed — please try again in a moment.',
      listTitle: 'Exports of this workspace',
      refresh: 'Refresh',
      loadFailed: 'We could not load the exports of this workspace.',
      empty: 'No export of this workspace has been made yet.',
      startedAt: 'Started {date}',
      availableUntil: 'Can be downloaded until {date}',
      download: 'Download the archive',
      downloadGone: 'This archive is no longer available. Start a new export to get one.',
      downloadUnavailable:
        'The archive cannot be fetched right now. It still exists — please try again in a moment.',
      downloadFailed: 'We could not download the archive.',
      status: {
        requested: 'Queued',
        exporting: 'Being prepared',
        exported: 'Ready to download',
        failed: 'Failed',
        expired: 'Deleted',
        unknown: 'Status unknown',
      },
    },
    // Erasure. The copy states the mechanism the service actually implements: a
    // request is filed, we review it, approval starts a period during which it
    // can still be withdrawn, and only then is the workspace erased. No number
    // of days is promised — the period is set when the request is approved, and
    // the real date is shown once there is one.
    erasure: {
      file: 'Request erasure of this workspace',
      loadFailed: 'We could not check whether an erasure has been requested for this workspace.',
      confirmTitle: 'Request erasure of this workspace?',
      confirmWhatGoes:
        'Everything in “{workspace}” would be erased permanently: every employee record, application, document, time entry and setting, for everyone who works in it. It cannot be brought back afterwards.',
      confirmProcess:
        'You are filing a request, not deleting anything yet. We review it, and once it is approved a waiting period starts. You can withdraw the request at any time until the erasure actually begins.',
      invoicesKept: 'The invoices stay, because the law requires us to keep them.',
      reason: 'Reason (optional)',
      keep: 'No, keep this workspace',
      confirmSubmit: 'Yes, request erasure',
      pendingReview: 'Erasure has been requested for this workspace. We are reviewing the request.',
      pendingUntil: 'Erasure has been requested for this workspace. It is erased after {date}.',
      paymentsPending:
        'This workspace is scheduled for deletion because its account has not been paid.',
      paymentsPendingUntil:
        'This workspace is scheduled for deletion because its account has not been paid. It is erased after {date}.',
      paymentsSettle:
        'Paying the outstanding invoice stops this deletion. You can also request the erasure yourself — then it goes ahead whatever happens with the payment.',
      paymentsCannotWithdraw:
        'This deletion was scheduled because the account has not been paid, so it cannot be withdrawn here. Pay the outstanding invoice to stop it.',
      confirmTakeover:
        'This workspace is already scheduled for deletion because the account has not been paid. Your request takes that deletion over: paying the invoice will then no longer stop it.',
      withdraw: 'Withdraw the erasure request',
      withdrawTitle: 'Withdraw the erasure request?',
      withdrawBody:
        'This workspace and all its data are kept. You can request erasure again at any time.',
      confirmType: 'Type the workspace name ({name}) to confirm',
      confirmMismatch:
        'The name you typed does not match this workspace. Check it and try again.',
      tooLate: 'The erasure has started and can no longer be withdrawn.',
      conflict: 'This workspace is already being erased, so nothing changed.',
      nothingOpen: 'There is no erasure request to withdraw for this workspace.',
      filed: 'Your erasure request has been filed. We will review it.',
      withdrawn: 'The erasure request has been withdrawn. This workspace stays as it is.',
    },
    // Closing the central account (issue 303 decisions 13, 14, 15). THE COPY IS
    // THE DELIVERABLE, and the line it has to draw has two sides, because closure
    // is neither "nothing goes" nor "everything goes":
    //   - What Lumi holds because SHE used Lumi goes with the account: her
    //     conversations with the assistant, the notifications we sent her and her
    //     settings for them, and her name and her words inside the support
    //     tickets and messages she wrote to us.
    //   - What an EMPLOYER holds about her stays. Every workspace is its own
    //     controller and keeps what it is required to keep, so both the screen
    //     before the click and the confirmation after it must still say where
    //     those records are and how to ask that company to erase them —
    //     otherwise "I closed my account" is read as "my data is gone".
    //   - Neither half is unconditional: a workspace under a legal hold is
    //     SKIPPED (user-api's fail-closed check before the fan-out), so what
    //     belongs to it stays. The copy has to survive that case being true
    //     rather than promise an erasure a hold can lawfully refuse.
    //
    // Decision 15 originally ruled the other way — closure reaches the central
    // identity ONLY — and this copy was written to that rule, which is why it
    // used to say closure "deletes nothing" an employer holds. The decision was
    // RE-TAKEN during issue 303 (see the plan's D15.1 amendment), after the
    // account-closure fan-out had already shipped: the traces her own use of Lumi
    // left in the platform's own services go too. If that fan-out is ever widened
    // or narrowed again, these nine catalogues move with it — they are the only
    // place a person is told what closing actually does.
    //
    // The refusal is not a dead end and never promises a queue: there is no
    // remembered closure, so it says come back.
    closure: {
      title: 'Closing your lumi account',
      intro:
        'This is your lumi account itself — your sign-in — and not one of the workspaces above.',
      open: 'Close my account',
      confirmTitle: 'Close your lumi account?',
      whatGoes:
        'Your sign-in disappears: this email address and password stop working, any company single sign-on you linked is disconnected, and you leave every workspace you belong to. What you did with lumi yourself goes with it: we delete your conversations with the assistant, the notifications we sent you and your settings for them, and we take your name and your words out of the support tickets and messages you wrote to us.',
      whatStays:
        'Your records with each employer stay with that employer. lumi runs the systems, but each company remains responsible for its own personnel and application data, and closing your account here does not delete it. If a workspace is caught up in a legal case or an official investigation, we have to keep what is connected to it: your tickets, messages and conversations from that workspace then stay until that is over.',
      howToErase:
        'To have the records an employer holds erased, ask that company directly. They answer that request themselves, and some of it they are required by law to keep for a while longer.',
      noUndo:
        'Closing cannot be undone, and nothing is held open for you. You can sign up again at any time with the same email address — that creates a new, empty account.',
      confirmType: 'Type your email address ({email}) to confirm',
      confirmMismatch: 'That is not the email address of this account. Check it and try again.',
      keep: 'No, keep my account',
      confirmSubmit: 'Yes, close my account',
      doneTitle: 'Your account is closed',
      doneBody:
        'You are signed out, and this email address and password no longer sign in anywhere in lumi. Your conversations with the assistant and the notifications we sent you are deleted, and the support tickets and messages you wrote to us no longer carry your name or your words.',
      doneStays:
        'Your records with each employer stay with that employer and are still theirs to manage. To have them erased, ask each company directly. And if a workspace is caught up in a legal case or an official investigation, what is connected to it stays with us until that is over.',
      doneMailed: 'We have sent you a confirmation by email.',
      doneNotMailed:
        'We could not send you the confirmation email. Your account is closed all the same.',
      blockedTitle: 'These workspaces still need an owner',
      blockedBody:
        'You are the only owner of the workspaces below, so we cannot close your account yet. A workspace without an owner can only be reopened by us, and everyone working in it would be locked out of their own data.',
      blockedTransfer:
        'Hand “{name}” over: open that workspace and transfer ownership to another member there.',
      blockedErasure: 'Or request the erasure of “{name}” in the list of your workspaces above.',
      blockedReturn:
        'We do not remember this attempt. Once no workspace needs you as its owner, come back to this page and close your account then.',
      unavailable:
        'Closing accounts is not working right now. Nothing was changed — please try again in a moment.',
      alreadyClosed: 'This account is already closed.',
      failed: 'That did not work. Your account was not closed — please try again in a moment.',
      confirmTypePhone: 'Type your mobile number ({phone}) in full to confirm',
      confirmMismatchPhone:
        'That is not the mobile number of this account. Check it and try again.',
      whatGoesPhone:
        'Your sign-in disappears: your passkeys and this mobile number stop working, any company single sign-on you linked is disconnected, and you leave every workspace you belong to. What you did with lumi yourself goes with it: we delete your conversations with the assistant, the notifications we sent you and your settings for them, and we take your name and your words out of the support tickets and messages you wrote to us.',
      noUndoPhone:
        'Closing cannot be undone, and nothing is held open for you. A workspace can invite you again with the same number — that creates a new, empty account.',
      doneBodyPhone:
        'You are signed out, and your passkeys and this mobile number no longer sign in anywhere in lumi. Your conversations with the assistant and the notifications we sent you are deleted, and the support tickets and messages you wrote to us no longer carry your name or your words.',
      doneScreenOnly: 'This screen is your confirmation — we do not send one by text message.',
    },
  },
};
